import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Nav from 'react-bootstrap/Nav'
import Navbar from 'react-bootstrap/Navbar'
import Dropdown from 'react-bootstrap/Dropdown'
import Button from 'react-bootstrap/Button'
import ImageLoader from '../ImageLoader'
import StickyNavbar from '../StickyNavbar'
import SocialButton from '../SocialButton'
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { SITE_URL, LOCALES, DEFAULT_LOCALE, absoluteUrl, ogLocale, organizationJsonLd } from '../../libs/utils/seo';

const RealEstatePageLayout = (props) => {
  const { t } = useTranslation('common');

  const router = useRouter();

  const { pathname, asPath, query, locale } = router; // Get the current route and locale
  const handleLanguageChange = (lang) => {
    router.push({ pathname, query }, asPath, { locale: lang });
  };


  // SEO: canonical + hreflang point at the page without its query string,
  // unless the page passes a canonicalPath (e.g. the catalog keeps its category)
  const canonicalPath = props.canonicalPath ?? asPath.split(/[?#]/)[0];
  const title = `Facilykey - ${props.pageTitle}`;
  const description = props.description || t("Facilykey : annonces immobilières au Maroc. Achetez, louez, vendez ou estimez votre bien avec un conseiller Facilykey.");
  const ogImage = props.image || `${SITE_URL}/images/header/casamarina.jpg`;
  const jsonLd = [organizationJsonLd, ...[].concat(props.jsonLd || [])];

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name='description' content={description} />
        {props.noindex && <meta name='robots' content='noindex, follow' />}
        <link rel='canonical' href={absoluteUrl(canonicalPath, locale)} />
        {LOCALES.map((lng) => (
          <link key={lng} rel='alternate' hrefLang={lng} href={absoluteUrl(canonicalPath, lng)} />
        ))}
        <link rel='alternate' hrefLang='x-default' href={absoluteUrl(canonicalPath, DEFAULT_LOCALE)} />
        <meta property='og:site_name' content='Facilykey' />
        <meta property='og:locale' content={ogLocale(locale)} />
        <meta property='og:type' content={props.ogType || 'website'} />
        <meta property='og:title' content={title} />
        <meta property='og:description' content={description} />
        <meta property='og:url' content={absoluteUrl(canonicalPath, locale)} />
        <meta property='og:image' content={ogImage} />
        <meta name='twitter:card' content='summary_large_image' />
        <script
          type='application/ld+json'
          // Escape "<" so listing text can never close the script tag
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
        />
      </Head>


      {/* Page wrapper for sticky footer
      Wraps everything except footer to push footer to the bottom of the page if there is little content */}
      <main className='page-wrapper'>

        {/* Navbar (main site header with branding and navigation) */}
        <Navbar as={StickyNavbar}
          // Rendered as <header> (banner landmark): role="navigation" is not allowed on it.
          // The menu below is the <nav> landmark instead.
          role={null}
          expand='lg'
          bg='Dark'
          className={`fixed-top${props.navbarExtraClass ? ` ${props.navbarExtraClass}` : ''}`}
        >
          <Container>
            <Navbar.Brand as={Link} href='/' className='me-3 me-xl-4'>
              <ImageLoader loading='eager' src='/images/logo/logo-dark.svg' width={136} height={33} placeholder={false} alt='Facilykey' />
            </Navbar.Brand>
            <Navbar.Toggle aria-controls='navbarNav' className='ms-auto' />
            <Button variant='sm text-accent d-none d-lg-block order-lg-3' href={"https://app.facilykey.com"}>
              <i className='fi-user me-2'></i>
              {t("Espace Pro")}
            </Button>

            <Button as={Link} href='/join-us' size='sm' className='primary d-none d-lg-block btn order-lg-3 ms-2'>
              {t("Devenir Conseiller")}
            </Button>

            <Navbar.Collapse id='navbarNav'>
              <Nav as='nav' aria-label={t('Navigation principale')} navbarScroll style={{ maxHeight: '35rem' }}>
                <Nav.Item>
                  <Nav.Link as={Link} href='/catalog?category=vendre' className='align-items-center pe-lg-4'>{t('Acheter')}</Nav.Link>
                </Nav.Item>
                <Nav.Item as={Dropdown}>
                  <Dropdown.Toggle as={Nav.Link}>{t('Louer')}</Dropdown.Toggle>
                  <Dropdown.Menu renderOnMount>
                    <Dropdown.Item as={Link} href='/catalog?category=location'>{t('Louer un bien')}</Dropdown.Item>
                    <Dropdown.Item as={Link} href='/add-rental'>{t('Mise en location')}</Dropdown.Item>
                  </Dropdown.Menu>
                </Nav.Item>
                {/* <Nav.Item>
                  <Nav.Link as={Link} href='/catalog?category=location' className='align-items-center pe-lg-4'>{t('Louer')}</Nav.Link>
                </Nav.Item> */}
                <Nav.Item>
                  <Nav.Link as={Link} href='/sell-property' className='align-items-center pe-lg-4'>{t('Vendre')}</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link as={Link} href='/estimate-property' className='align-items-center pe-lg-4'>{t('Estimer')}</Nav.Link>
                </Nav.Item>
                {/* <Nav.Item>
                  <Nav.Link as={Link} href='/add-rental' className='align-items-center pe-lg-4'>{t('Mise en location')}</Nav.Link>
                </Nav.Item> */}
                <Nav.Item>
                  <Nav.Link as={Link} href='https://app.facilykey.com' className='align-items-center d-lg-none pe-lg-4'>{t('Espace Pro')}</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link as={Link} href='/join-us' className='primary d-lg-none align-items-center pe-lg-4'>{t('Devenir Conseiller')}</Nav.Link>
                </Nav.Item>
                <Nav.Item as={Dropdown}>
                  {/* One interactive element (like the "Louer" toggle): a div with aria-expanded
                      wrapping a link was an invalid ARIA pattern */}
                  <Dropdown.Toggle as={Nav.Link} className={'d-flex justify-content-center align-items-center'}>
                    <ImageLoader src={`/images/illustrations/flag-${locale.toString() ?? 'fr'}.svg`} width={26} height={26} alt='' className={'ms-1 pe-1'} />
                    <span>
                      {locale.toLocaleUpperCase().toString() ?? ' FR'}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu renderOnMount>
                    <Dropdown.Item as={'button'} active={locale === 'fr'} onClick={() => handleLanguageChange('fr')} className={'d-flex align-items-center fw-semibold'}>
                      <ImageLoader src='/images/illustrations/flag-fr.svg' width={26} height={26} alt='' className={'ms-1 pe-1'} />
                      {t("Français")}
                    </Dropdown.Item>
                    <Dropdown.Item as={'button'} active={locale === 'en'} onClick={() => handleLanguageChange('en')} className={'d-flex align-items-center fw-semibold'}>
                      <ImageLoader src='/images/illustrations/flag-en.svg' width={26} height={26} alt='' className={'ms-1 pe-1'} />
                      {t("Anglais")}
                    </Dropdown.Item>
                    <Dropdown.Item as={'button'} active={locale === 'ar'} onClick={() => handleLanguageChange('ar')} className={'d-flex align-items-center fw-semibold'}>
                      <ImageLoader src='/images/illustrations/flag-ar.svg' width={26} height={26} alt='' className={'ms-1 pe-1 rounded'} />
                      {t("العربية")}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Nav.Item>
              </Nav>
            </Navbar.Collapse>

            {/*<Dropdown>*/}
            {/*  <Dropdown.Toggle*/}
            {/*      size="sm"*/}
            {/*      className="order-lg-4 ms-2"*/}
            {/*      variant="outline-secondary"*/}
            {/*  >*/}
            {/*    {locale?.toUpperCase() || 'FR'}*/}
            {/*  </Dropdown.Toggle>*/}
            {/*  <Dropdown.Menu className="my-1">*/}
            {/*    <Dropdown.Item   active={locale === 'fr'} onClick={() => handleLanguageChange('fr')}>FR</Dropdown.Item>*/}
            {/*    <Dropdown.Item   active={locale === 'en'} onClick={() => handleLanguageChange('en')}>EN</Dropdown.Item>*/}
            {/*    /!*<Dropdown.Item   active={locale === 'es'} onClick={() => handleLanguageChange('es')}>ES</Dropdown.Item>*!/*/}
            {/*  </Dropdown.Menu>*/}
            {/*</Dropdown>*/}


          </Container>
        </Navbar>
        {/* Page content */}
        {props.children}
      </main>


      {/* Footer */}
      <footer className='footer bg-secondary pt-5'>
        <Container className='pt-lg-4 pb-4'>
          <Row className='mb-5 pb-md-3 pb-lg-4'>
            <div className='mb-lg-0 mb-4'>
              <div className='d-flex flex-sm-row flex-column justify-content-between mx-n2'>

                {/* Logo + contacts */}
                <div className='mb-sm-0 mb-4 px-2'>
                  <Link href='/' className='d-inline-flex mb-4'>
                    <ImageLoader src='/images/logo/logo-dark.svg' width={123} height={30} placeholder={false} alt='Facilykey' />
                  </Link>
                  <Nav className='flex-column mb-sm-4 mb-2'>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} target={'_blank'} href='tel:+212615348229'
                        className='fw-normal'>
                        <i className="fi-phone mt-n1 me-2 align-middle opacity-60"></i>
                        +212 6 15 34 82 29
                      </Nav.Link>
                      <Nav.Link className={"fw-normal"} target={'_blank'} href={'mailto:contact@facilykey.com'}>
                        <i className="fi-mail mt-n1 me-2 align-middle opacity-60"></i>
                        contact@facilykey.com
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                  <div className='pt-2 ps-2'>
                    <SocialButton href='https://web.facebook.com/profile.php?id=100094181588390' variant='solid' target={'_blank'} brand='facebook' roundedCircle className='me-2 mb-2' />
                    {/*<SocialButton href='#' variant='solid' brand='twitter' roundedCircle className='me-2 mb-2' />*/}
                    <SocialButton href='https://www.instagram.com/facilykey.official' variant='solid' target={'_blank'} brand='instagram' roundedCircle className='me-2 mb-2' />
                    {/*<SocialButton href='#' variant='solid' brand='linkedin' roundedCircle className='mb-2' />*/}
                  </div>
                </div>

                {/* Quick links */}
                <div className='mb-sm-0 mb-4 px-2'>
                  <h4 className='h5'>{t('Le site')}</h4>
                  <Nav className='flex-column'>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/catalog?category=vendre'
                        active={false}
                        className='p-0 fw-normal'>{t('Acheter un bien immobilier')}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/sell-property' active={false} className='p-0 fw-normal'>{t('Vendre un bien immobilier')}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/estimate-property' active={false} className='p-0 fw-normal'>{t('Estimer votre bien')}</Nav.Link>
                    </Nav.Item>
                    {/*<Nav.Item className='mb-2'>*/}
                    {/*<Nav.Link as={Link} href='/vendor-list' active={false} className='p-0 fw-normal'>Trouver un agent</Nav.Link>*/}
                    {/*</Nav.Item>*/}
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/join-us' active={false} className='p-0 fw-normal'>{t('Devenir agent Facilykey')}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>

                {/* About links */}
                <div className='px-2'>
                  <h4 className='h5'>{t('Le réseau')}</h4>
                  <Nav className='flex-column'>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/about' active={router.pathname === '/about'} className='p-0 fw-normal'>{t('Qui sommes-nous?')}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/join-us' active={router.pathname === '/join-us'} className='p-0 fw-normal'>{t('Nous rejoindre')}</Nav.Link>
                    </Nav.Item>
                    {/*<Nav.Item className='mb-2'>*/}
                    {/*  <Nav.Link as={Link} href='#' active={false} className='p-0 fw-normal'>La charte Facilykey</Nav.Link>*/}
                    {/*</Nav.Item>*/}
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/contacts' active={router.pathname === '/contacts'} className='p-0 fw-normal'>{t('Contactez-nous')}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='https://app.facilykey.com/mentions-legales' target={'_blank'} className='p-0 fw-normal'>{t('Mentions légales')}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='https://app.facilykey.com/cgu' target={'_blank'} className='p-0 fw-normal'>{t('CGU')}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/cookies' active={router.pathname === '/cookies'} className='p-0 fw-normal'>{t('Gestion des cookies')}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>

                {/* About links */}
                <div className='px-2'>
                  <h4 className='h5'>{t('Acheter')}</h4>
                  <Nav className='flex-column'>
                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/catalog?category=vendre&city=23' className='p-0 fw-normal'>{t('Immobilier à vendre à')} {t('Tanger')}</Nav.Link>
                    </Nav.Item>

                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/catalog?category=vendre&city=11' className='p-0 fw-normal'>{t('Immobilier à vendre à')} {t('Marrakech')}</Nav.Link>
                    </Nav.Item>

                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/catalog?category=vendre&city=17' className='p-0 fw-normal'>{t('Immobilier à vendre à')} {t('Rabat')}</Nav.Link>
                    </Nav.Item>

                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/catalog?category=vendre&city=14' className='p-0 fw-normal'>{t('Immobilier à vendre à')} {t('Mohammedia')}</Nav.Link>
                    </Nav.Item>

                    <Nav.Item className='mb-2'>
                      <Nav.Link as={Link} href='/catalog?category=vendre&city=8' className='p-0 fw-normal'>{t('Immobilier à vendre à')} {t('Fès')}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>
              </div>
            </div>
          </Row>

          {/* Copyright */}
          <div className='text-center fs-sm pt-4 mt-3 pb-2'>
            &copy; {new Date().getFullYear() + " " + t('FacilyKey. Tous droits réservés.')}
          </div>
        </Container>
      </footer>
    </>
  )
}

export default RealEstatePageLayout


